import { usePage } from "@inertiajs/react"
import { CalendarCheck, Compass, PlayCircle, Video } from "lucide-react"
import { useEffect, useMemo } from "react"

import {
  MobileTabBar,
  isPrefixPath,
  normalizePath,
} from "@/components/mobile-tab-bar"
import { useSidebar } from "@/components/ui/sidebar"
import {
  memberEventsExplorePath,
  memberEventsUpcomingPath,
  memberMaterialsPath,
  memberPath,
  memberReplaysPath,
} from "@/routes"
import type { SharedData } from "@/types"

export function PwaTabBar() {
  const page = usePage<SharedData>()
  const { openMobile, setOpenMobile } = useSidebar()
  const currentPath = normalizePath(page.url)
  const sidebarCounts = page.props.member_sidebar_counts
  const access = page.props.access_status
  const module = page.props.module
  const ownsCurrentProgram =
    (typeof access === "object" &&
      access !== null &&
      "has_access" in access &&
      access.has_access === true) ||
    (typeof module === "object" &&
      module !== null &&
      "accessible" in module &&
      module.accessible === true) ||
    page.props.user_has_purchased === true

  const liveHref = memberPath()
  const onDemandHref = memberReplaysPath()
  const exploreHref = memberEventsExplorePath()
  const myProgramsHref = memberEventsUpcomingPath()
  const programsRoot = exploreHref.replace(/\/explore$/, "")
  const materialsHref = memberMaterialsPath()

  const tabs = useMemo(() => {
    const liveActive =
      currentPath === liveHref || isPrefixPath(currentPath, `${liveHref}/live`)
    const onDemandActive = isPrefixPath(currentPath, onDemandHref)
    const hasMyPrograms = [
      sidebarCounts?.upcoming,
      sidebarCounts?.completed,
      sidebarCounts?.materials,
    ].some((count) => (count ?? 0) > 0)
    const collectionActive = [
      myProgramsHref,
      `${programsRoot}/completed`,
      materialsHref,
    ].some((path) => isPrefixPath(currentPath, path))
    const programDetail =
      isPrefixPath(currentPath, programsRoot) &&
      !isPrefixPath(currentPath, exploreHref)
    const myProgramsActive =
      hasMyPrograms &&
      (collectionActive || (programDetail && ownsCurrentProgram))
    const exploreActive =
      isPrefixPath(currentPath, exploreHref) ||
      (programDetail && !myProgramsActive)

    return [
      {
        id: "live",
        title: "Live",
        href: liveHref,
        icon: Video,
        active: liveActive && !openMobile,
        badge: 0,
      },
      {
        id: "on-demand",
        title: "On-demand",
        href: onDemandHref,
        icon: PlayCircle,
        active: onDemandActive && !openMobile,
        badge: sidebarCounts?.on_demand ?? 0,
      },
      {
        id: "explore",
        title: "Explore",
        href: exploreHref,
        icon: Compass,
        active: exploreActive && !openMobile,
        badge: sidebarCounts?.programs ?? 0,
      },
      ...(hasMyPrograms
        ? [
            {
              id: "my-programs",
              title: "My programs",
              href: myProgramsHref,
              icon: CalendarCheck,
              active: myProgramsActive && !openMobile,
              badge: sidebarCounts?.upcoming ?? 0,
            },
          ]
        : []),
    ]
  }, [
    currentPath,
    liveHref,
    onDemandHref,
    openMobile,
    exploreHref,
    myProgramsHref,
    programsRoot,
    materialsHref,
    sidebarCounts,
    ownsCurrentProgram,
  ])

  useEffect(() => {
    setOpenMobile(false)
  }, [currentPath, setOpenMobile])

  return (
    <MobileTabBar
      tabs={tabs}
      menuOpen={openMobile}
      onMenuClick={() => setOpenMobile(!openMobile)}
      onNavigate={() => setOpenMobile(false)}
    />
  )
}
